import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { onServiceWorkerStatus, applyPendingUpdate } from '../pwa/registerSW';
import { sessionRepository } from '../data/repositories/sessionRepository';
import { useFocusTrap } from '../ui/components/useFocusTrap';
import styles from './UpdateRequired.module.css';

/**
 * Mandatory "update available" screen — Phase C.
 *
 * Evolved from the earlier dismissible `UpdateBanner`, not replaced
 * alongside it: `registerSW.ts` remains the single source of truth for "is
 * an update pending" (`onServiceWorkerStatus`), and this stays its only
 * consumer — there is deliberately no second, competing piece of update UI
 * anywhere else in the app.
 *
 * What changed is what happens once that's true. A banner could be ignored
 * indefinitely; this blocks normal use instead, because a build can be
 * pending indefinitely on a device nobody force-refreshes — and API routes
 * eventually move on. The one thing that must never happen is interrupting
 * an in-progress workout (Phase 0 §9.3): `sessionRepository.getOpenSession()`
 * — the exact same check the banner already used to suppress itself — now
 * gates this screen's very existence, re-checked on every route change,
 * which is what actually lifts the suppression the moment a workout finishes
 * or is abandoned and the user navigates away.
 *
 * Integration hardening pass: `handleUpdate()` used to set `applying = true`
 * and never resolve that state on its own — `applyPendingUpdate()` mostly
 * only *sends* the skip-waiting message; the actual reload happens later,
 * asynchronously, from a `controlling` event listener this component has no
 * visibility into. If that event never fires (the new worker fails to
 * activate — rare, but real), the button stayed disabled forever with no way
 * out. `APPLY_TIMEOUT_MS` bounds that wait and surfaces a retry state
 * instead — still inside the same non-dismissible overlay, never a way to
 * fall back to the old version.
 */
const APPLY_TIMEOUT_MS = 15_000;

export function UpdateRequired(): React.JSX.Element | null {
  const location = useLocation();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // Starts `true` (assume a workout might be open) rather than `false`, so
  // this never flashes visible for a beat during an active workout while the
  // async `getOpenSession()` lookup below is still resolving.
  const [workoutOpen, setWorkoutOpen] = useState(true);
  const [applyState, setApplyState] = useState<'idle' | 'applying' | 'error'>('idle');
  const panelRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearApplyTimeout(): void {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }

  // Belt-and-braces only: a *successful* update ends in a real
  // `window.location.reload()`, which tears down this whole JS context —
  // there is no in-app "it worked" moment to clear the timeout from. This
  // covers the other way the component can go away: the workout-deferral
  // gate flipping back on, or any other unmount, mid-wait.
  useEffect(() => {
    return () => clearApplyTimeout();
  }, []);

  useEffect(
    () => onServiceWorkerStatus((status) => setUpdateAvailable(status.updateAvailable)),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    void sessionRepository.getOpenSession().then((session) => {
      if (!cancelled) setWorkoutOpen(session !== undefined);
    });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const blocking = updateAvailable && !workoutOpen;

  // The hook itself is unconditional (React's rules of hooks); the trap it
  // installs is a no-op whenever this returns `null` below, since nothing
  // ever focuses `panelRef` in that case and the effect's own guard only
  // reacts to Tab.
  useFocusTrap(panelRef);

  useEffect(() => {
    if (blocking) panelRef.current?.focus();
  }, [blocking]);

  if (!blocking) return null;

  async function handleUpdate(): Promise<void> {
    setApplyState('applying');
    clearApplyTimeout();
    // A real update ends this whole JS context (a `window.location.reload()`
    // from the SW's own `controlling` listener) — so this timer only ever
    // fires when that *didn't* happen. 15s is generous for what is normally
    // a fast, local, no-network activation of an already-fully-downloaded
    // worker, so it doesn't false-fire on a merely slow device, while still
    // bounding the wait instead of leaving the button disabled forever.
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setApplyState('error');
    }, APPLY_TIMEOUT_MS);

    try {
      await applyPendingUpdate();
      // Usually resolves quickly regardless of whether the worker actually
      // activates afterward (see the module doc) — if a real reload is
      // genuinely underway, it will happen out from under this component
      // before the timeout above ever gets the chance to fire.
    } catch {
      clearApplyTimeout();
      setApplyState('error');
    }
  }

  const applying = applyState === 'applying';
  const hasError = applyState === 'error';

  return createPortal(
    <div className={styles.overlay}>
      <div
        ref={panelRef}
        className={styles.panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="update-required-heading"
        aria-describedby="update-required-body"
        tabIndex={-1}
      >
        <h1 id="update-required-heading" className={styles.heading}>
          Update Available
        </h1>
        <p id="update-required-body" className={styles.body}>
          A new version of LiftX is available. Please update LiftX to continue using the latest
          version.
        </p>
        {hasError && (
          <p role="alert" className={styles.error}>
            The update did not complete. Check your connection and try again.
          </p>
        )}
        <button
          type="button"
          className={styles.button}
          disabled={applying}
          onClick={() => void handleUpdate()}
          autoFocus
        >
          {hasError ? 'Try Again' : 'Update LiftX'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
